import { useContext, useEffect } from 'react'
import { useAutoUpdatingRef } from '@quickmd-llc/frontend-utils'
import { NavigationGuardContext, type NavigationGuard } from './NavigationGuardContext'

const useNavigationGuardContext = () => {
  const ctx = useContext(NavigationGuardContext)
  if (!ctx) throw new Error('NavigationGuard hooks must be used within a NavigationGuardProvider')
  return ctx
}

/** Consumed by the layout: the ref holding the currently-registered guard (or null when none). */
export const useNavigationGuardRef = () => useNavigationGuardContext().guardRef

/**
 * Register a navigation guard for the lifetime of the calling component. The guard closure is kept
 * fresh via a ref, so it always sees the latest props/state without re-registering on every render.
 */
export const useRegisterNavigationGuard = (guard: NavigationGuard) => {
  const { guardRef } = useNavigationGuardContext()
  const latestGuard = useAutoUpdatingRef(guard)

  useEffect(() => {
    const stableGuard: NavigationGuard = (destination) => latestGuard.current(destination)
    guardRef.current = stableGuard
    return () => {
      if (guardRef.current === stableGuard) guardRef.current = null
    }
  }, [guardRef, latestGuard])
}
